/**
 * App — Main authenticated application shell with routing.
 */

import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import NavBar from "./components/NavBar";
import { RequireRole } from "./components/RequireRole";
import PortalPage from "./pages/PortalPage";
import { UserRole } from "./models/types";
import { Spinner } from "react-bootstrap";

// Lazy-loaded pages (admin and viewer)
const MultiviewPage = lazy(() => import("./pages/MultiviewPage"));
const UserAdminPage = lazy(() => import("./pages/UserAdminPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const ManageBUPage = lazy(() => import("./pages/ManageBUPage"));
const AddClientPage = lazy(() => import("./pages/AddClientPage"));
const EditClientPage = lazy(() => import("./pages/EditClientPage"));
const ManageClientPage = lazy(() => import("./pages/ManageClientPage"));
const ManageEventPage = lazy(() => import("./pages/ManageEventPage"));
const ManageElementPage = lazy(() => import("./pages/ManageElementPage"));

const AdminLayout = lazy(() => import("./containers/AdminPanel/AdminLayout"));
const BusinessUnitList = lazy(() => import("./containers/AdminPanel/BusinessUnitList"));
const GuestInviteList = lazy(() => import("./containers/AdminPanel/GuestInviteList"));

function PageLoader() {
  return (
    <div className="d-flex justify-content-center py-5">
      <Spinner animation="border" variant="primary" />
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const isViewer = location.pathname.startsWith("/viewer");

  return (
    <>
      {!isViewer && <NavBar />}
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Portal — main landing page */}
        <Route path="/" element={<PortalPage />} />

        {/* Stream viewer */}
        <Route path="/viewer" element={<MultiviewPage />} />

        {/* Profile and password change */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />

        {/* User Admin — standalone page */}
        <Route
          path="/admin/users"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <UserAdminPage />
            </RequireRole>
          }
        />

        {/* Manage Business Unit — standalone page */}
        <Route
          path="/admin/business-units/:id"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <ManageBUPage />
            </RequireRole>
          }
        />

        {/* Add Client to Business Unit */}
        <Route
          path="/admin/business-units/:id/clients/new"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <AddClientPage />
            </RequireRole>
          }
        />

        {/* Edit Client */}
        <Route
          path="/admin/business-units/:id/clients/:clientId/edit"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <EditClientPage />
            </RequireRole>
          }
        />

        {/* Manage Event */}
        <Route
          path="/admin/business-units/:id/clients/:clientId/events/:eventId"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <ManageEventPage />
            </RequireRole>
          }
        />

        {/* Manage Element (event stream element details) */}
        <Route
          path="/admin/business-units/:id/clients/:clientId/events/:eventId/elements/:elementIndex"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <ManageElementPage />
            </RequireRole>
          }
        />

        {/* Manage Client */}
        <Route
          path="/admin/business-units/:id/clients/:clientId"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <ManageClientPage />
            </RequireRole>
          }
        />

        {/* Admin panel (Admin+ required) */}
        <Route
          path="/admin"
          element={
            <RequireRole minRole={UserRole.ADMIN}>
              <AdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="business-units" element={<BusinessUnitList />} />
          <Route path="invites" element={<GuestInviteList />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}
